import { ExternalCalendarEvent, IDPrefixes } from '@griply/types';
import {
  addDays,
  differenceInDays,
  eachDayOfInterval,
  endOfDay,
  format,
  startOfDay,
} from 'date-fns';

import { createCompositeId } from './encoded-ids/composite-id';
import { parseDateKey } from './format-date-key';

export type CreateDailyEventsRange = {
  start: Date;
  end: Date;
};

type EventSpan = { startDate: Date; endDate: Date; isAllDay: boolean };

const DATE_KEY_FORMAT = 'yyyy-MM-dd';
const NAIVE_DATE_TIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ss";

const toDate = (boundary: ExternalCalendarEvent['start']): Date | null => {
  if (boundary.dateTime) {
    return new Date(boundary.dateTime);
  }
  return boundary.date ? parseDateKey(boundary.date) : null;
};

const toEventSpan = (event: ExternalCalendarEvent): EventSpan | null => {
  const startDate = toDate(event.start);
  const endDate = toDate(event.end);
  if (!startDate || !endDate) {
    return null;
  }
  return {
    startDate,
    endDate,
    isAllDay: !!event.start.date && !!event.end.date,
  };
};

// An all-day end date is exclusive, so the day count is the plain difference; a timed span counts every touched day.
const getEventDayCount = ({ startDate, endDate, isAllDay }: EventSpan) =>
  isAllDay
    ? differenceInDays(endDate, startDate)
    : eachDayOfInterval({ start: startDate, end: endDate }).length;

// Only the offsets inside the visible range are materialized, so a multi-year event costs a week of rows in a week view.
const getVisibleDayOffsets = (
  range: CreateDailyEventsRange,
  startDate: Date,
  lastDayOffset: number,
) => ({
  first: Math.max(
    0,
    differenceInDays(startOfDay(range.start), startOfDay(startDate)),
  ),
  last: Math.min(
    lastDayOffset,
    differenceInDays(startOfDay(range.end), startOfDay(startDate)),
  ),
});

const toDailyId = (event: ExternalCalendarEvent, dayOffset: number) =>
  createCompositeId(IDPrefixes.MultiDayEvent, [event.id, String(dayOffset)]);

const toAllDayInstance = (
  event: ExternalCalendarEvent,
  currentDay: Date,
  dayOffset: number,
): ExternalCalendarEvent => {
  const date = format(currentDay, DATE_KEY_FORMAT);
  return {
    ...event,
    id: toDailyId(event, dayOffset),
    start: { date },
    end: { date },
  };
};

const toTimedInstance = (
  event: ExternalCalendarEvent,
  currentDay: Date,
  dayOffset: number,
  lastDayOffset: number,
): ExternalCalendarEvent => {
  const dayStart = format(startOfDay(currentDay), NAIVE_DATE_TIME_FORMAT);
  const dayEnd = format(endOfDay(currentDay), NAIVE_DATE_TIME_FORMAT);
  return {
    ...event,
    id: toDailyId(event, dayOffset),
    start: {
      dateTime: dayOffset === 0 ? event.start.dateTime ?? dayStart : dayStart,
    },
    end: {
      dateTime:
        dayOffset === lastDayOffset ? event.end.dateTime ?? dayEnd : dayEnd,
    },
  };
};

export const createDailyEventsFromExternalEvents = (
  externalEvents: ExternalCalendarEvent[],
  range: CreateDailyEventsRange,
): ExternalCalendarEvent[] =>
  externalEvents.flatMap((event) => {
    const span = toEventSpan(event);
    if (!span) {
      return [event];
    }
    const dayCount = getEventDayCount(span);
    if (dayCount <= 1) {
      return [event];
    }
    const lastDayOffset = dayCount - 1;
    const { first, last } = getVisibleDayOffsets(
      range,
      span.startDate,
      lastDayOffset,
    );
    if (first > last) {
      return [];
    }
    return Array.from({ length: last - first + 1 }, (_, index) => {
      const dayOffset = first + index;
      const currentDay = addDays(span.startDate, dayOffset);
      return span.isAllDay
        ? toAllDayInstance(event, currentDay, dayOffset)
        : toTimedInstance(event, currentDay, dayOffset, lastDayOffset);
    });
  });
